import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { PageLoading } from "@/components/common/Loading";
import { EmptyState } from "@/components/common/EmptyState";
import { RagBadge } from "@/components/common/RagBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { toast } from "sonner";
import dayjs from "dayjs";

const SESSION_STATUS = {
  planned: { label: "Planned", rag: "blue" },
  preparation_required: { label: "Preparation required", rag: "amber" },
  completed: { label: "Completed", rag: "green" },
  cancelled: { label: "Cancelled", rag: "grey" },
};

export default function Coaching() {
  const { orgId } = useOrg();
  const { canEdit } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = () => api.get(`/coaching?org_id=${orgId}`).then((r) => setSessions(r.data));
  useEffect(() => { if (orgId) { setSessions(null); load(); } }, [orgId]);

  if (!sessions) return <PageLoading />;

  const upcoming = sessions.filter((s) => ["planned", "preparation_required"].includes(s.status));
  const past = sessions.filter((s) => ["completed", "cancelled"].includes(s.status));

  return (
    <div data-testid="coaching-page">
      <PageHeader
        eyebrow="Coaching"
        title="Coaching Sessions"
        subtitle="Prepare, run and follow up on every coaching session with your Amden consultant."
        icon="Users"
        actions={canEdit && (
          <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="new-session-btn">
            <Icons.Plus size={16} className="mr-1.5" /> Schedule session
          </Button>
        )}
      />

      {sessions.length === 0 ? (
        <EmptyState icon="Users" title="No sessions yet" description="Schedule your first coaching session."
          action={canEdit && <Button onClick={() => setCreating(true)} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]">Schedule session</Button>} />
      ) : (
        <div className="space-y-8">
          {upcoming.length > 0 && (
            <div>
              <h2 className="mb-3 font-display text-lg font-bold text-[#0B1729]">Upcoming</h2>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {upcoming.map((s) => <SessionCard key={s.id} s={s} onClick={() => navigate(`/coaching/${s.id}`)} highlight />)}
              </div>
            </div>
          )}
          <div>
            <h2 className="mb-3 font-display text-lg font-bold text-[#0B1729]">History</h2>
            {past.length === 0 ? <p className="text-sm text-[#1E2D44]/50">No completed sessions yet.</p> : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {past.map((s) => <SessionCard key={s.id} s={s} onClick={() => navigate(`/coaching/${s.id}`)} />)}
              </div>
            )}
          </div>
        </div>
      )}

      {creating && <CreateSessionDialog orgId={orgId} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function SessionCard({ s, onClick, highlight }) {
  return (
    <div onClick={onClick} className={`amden-card amden-card-hover cursor-pointer p-6 ${highlight ? "border-l-4 border-l-[#FFB000]" : ""}`} data-testid={`session-card-${s.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <RagBadge rag={SESSION_STATUS[s.status]?.rag}>{SESSION_STATUS[s.status]?.label}</RagBadge>
          <h3 className="mt-2 font-display text-lg font-bold text-[#0B1729]">{s.title}</h3>
        </div>
        <div className="text-right text-xs text-[#1E2D44]/50">
          <div className="font-medium text-[#0B1729]">{fmtDate(s.date)}</div>
          <div>{dayjs(s.date).format("HH:mm")}</div>
        </div>
      </div>
      <p className="mt-2 text-sm text-[#1E2D44]/60 line-clamp-2">{s.objective}</p>
      <div className="mt-4 flex items-center gap-4 border-t border-[#EEF1F6] pt-3 text-xs text-[#1E2D44]/55">
        <span className="flex items-center gap-1"><Icons.User size={13} /> {s.consultant_name}</span>
        <span className="flex items-center gap-1"><Icons.Video size={13} /> {s.meeting_type}</span>
      </div>
    </div>
  );
}

function CreateSessionDialog({ orgId, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: "", date: dayjs().add(3, "day").format("YYYY-MM-DDTHH:mm"),
    meeting_type: "Video call", location: "", objective: "", status: "preparation_required",
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title) return toast.error("Add a title");
    setSaving(true);
    try {
      await api.post(`/coaching?org_id=${orgId}`, { ...form, date: new Date(form.date).toISOString() });
      toast.success("Session scheduled");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Schedule coaching session</DialogTitle></DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1" placeholder="e.g. Monthly Review" data-testid="session-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date & time</Label><Input type="datetime-local" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1" /></div>
            <div><Label>Meeting type</Label>
              <Select value={form.meeting_type} onValueChange={(v) => setForm({ ...form, meeting_type: v })}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{["Video call", "On-site", "Phone", "In person"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Location / link</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="mt-1" /></div>
          <div><Label>Objective</Label><Textarea value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })} className="mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="save-session-btn">{saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
